'use client';

import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import { useSearchSharingResultByQuery } from '@/app/services/sharingResultApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { usePeriod } from '@/context/PeriodContext';
import { TableCell } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IndiretoTableProps {
  originProject: Project | null;
  destinationProject: Project | null;
  collaborator: Collaborator | null;
  rowsPerPage: number;
  setRowsPerPage: (p: number) => void;
  rowsPageOptions: number[];
}

const smallCellSx = {
  ...bodyCellSx,
  fontSize: 'caption.fontSize', // Smaller font size for this table
};

export default function IndirectTable({
  originProject,
  destinationProject,
  collaborator,
  rowsPerPage,
  setRowsPerPage,
  rowsPageOptions,
}: IndiretoTableProps) {
  const { selectedPeriod } = usePeriod();
  const [page, setPage] = useState(0);
  const { data, isLoading: loading } = useSearchSharingResultByQuery(
    {
      originProjectSearchTerm: originProject?.name || '',
      destiniProjectSearchTerm: destinationProject?.name || '',
      personSearchTerm: collaborator?.badge || '',
      period: selectedPeriod?.date || '',
      page,
      size: rowsPerPage,
    },
    { skip: !selectedPeriod },
  );
  const { t } = useTranslation('finalVersion');

  const columns: Column[] = [
    { key: 'collaborator', label: t('collaborator') },
    { key: 'position', label: t('position') },
    { key: 'originProj', label: t('origin.proj') },
    { key: 'destinyProj', label: t('destiny.proj') },
    { key: 'sharing', label: `${t('sharing')}`, align: 'center' },
    { key: 'type', label: t('type'), align: 'center' },
    { key: 'value', label: `${t('value')}` },
  ];

  const renderRow = (row: any) => (
    <>
      <TableCell sx={smallCellSx}>
        <TruncatedTextWithTooltip value={`${row.badge} - ${row.name}`} size={37} sx={smallCellSx} />
      </TableCell>
      <TableCell sx={smallCellSx}>{row.position}</TableCell>
      <TableCell sx={smallCellSx}>
        <TruncatedTextWithTooltip
          value={`${row.originProjectCode} - ${row.originProjectName}`}
          size={37}
          sx={smallCellSx}
        />
      </TableCell>
      <TableCell sx={smallCellSx}>
        <TruncatedTextWithTooltip
          value={`${row.destiniProjectCode} - ${row.destiniProjectName}`}
          size={37}
          sx={smallCellSx}
        />
      </TableCell>
      <TableCell sx={{ ...smallCellSx, textAlign: 'center' }}>{`${formatterPercentualValue(row.percent)}`}</TableCell>
      <TableCell sx={{ ...smallCellSx, textAlign: 'center' }}>{row.type}</TableCell>
      <TableCell sx={smallCellSx}>{`${formatterMonetaryValue(row.value)}`}</TableCell>
    </>
  );

  // Generate unique key using multiple fields
  const getRowKey = (row: any) => `${row.cpf}-${row.destiniProjectCode}-${row.originProjectCode}-${row.percent}`;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: data?.totalElements || 0,
    rowsPerPageOptions: rowsPageOptions,
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('de')} ${count}`,
    labelRowsPerPage: t('itens.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={data?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      loading={loading}
      pagination={paginationConfig}
    />
  );
}
